"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { useWebSiteContext } from "@core/helpers/client/contexts/WebSiteContext";
import useIsHydrated from "@core/helpers/client/hooks/useIsHydrated";
import { build_login_url, is_logout_navigation_pending } from "@core/helpers/auth-redirect";

/**
 * Client guard of private pages (account, checkout). A guest who opens the page directly is redirected by the server
 * (`require_login`); this guard covers a page that is already open when the login goes away: logout in another tab,
 * an expired token AuthContext clears, a Back to a cached page. Then it replaces the page with the login page, coming
 * back here afterwards. A logout started with `mark_logout_navigation()` is left alone (its button picks the destination).
 * Moved from chawk-bazar / ciseco / jument (`components/pages/Account/_authGuard.jsx`).
 *
 * @param props.hide_when_guest true (jument, default): after hydration a guest sees `guest_fallback` instead of the
 *   content. false (chawk-bazar / ciseco): the content always renders, only the redirect runs.
 *   `is_auth` is false during SSR (the token is read with js-cookie), so the server HTML always has the content.
 * @param props.guest_fallback what a guest sees while redirected (default null)
 * @param props.auth_path login page path; default `web_route_names.auth`
 */
export const AccountAuthGuard = ({ children, hide_when_guest = true, guest_fallback = null, auth_path }) => {
	const { is_auth } = useAuthContext() || {};
	const { web_route_names } = useWebSiteContext() || {};
	const router = useRouter();
	const pathname = usePathname();
	const hydrated = useIsHydrated();
	const login_path = auth_path || web_route_names?.["auth"];

	useEffect(() => {
		if (is_auth || is_logout_navigation_pending()) return;
		router.replace(build_login_url(login_path, `${pathname}${window.location.search}`));
	}, [is_auth, pathname, login_path, router]);

	if (hide_when_guest && hydrated && !is_auth) return guest_fallback;
	return children;
};

export default AccountAuthGuard;
